import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { HiTrash, HiExclamationTriangle, HiFolder, HiDocument } from 'react-icons/hi2';
import { RootState } from '../../../redux/reducers';
import {
  setShowDeleteConfirmModal,
  removeItem,
  removeDirectory,
  removeItems,
  setLoading,
  setStorageUsage,
  setItems,
  setDirectoryTree,
} from '../../../redux/slices/storageSlice';
import storageService from '../../../services/storageService';
import Button from '../buttons/Button';

interface DeleteConfirmModalProps {
  isOpen: boolean;
}

export default function DeleteConfirmModal({ isOpen }: DeleteConfirmModalProps): JSX.Element | null {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { selectedItemIds, selectedDirectoryIds, items, allDirectories, currentDirectoryId } = useSelector(
    (state: RootState) => state.storage
  );

  if (!isOpen) return null;

  const selectedItems = items.filter((item) => selectedItemIds.includes(item.id));
  const selectedDirs = allDirectories.filter((dir) => selectedDirectoryIds.includes(dir.id));
  const totalSelected = selectedItemIds.length + selectedDirectoryIds.length;

  const handleConfirm = async () => {
    try {
      dispatch(setLoading(true));

      // Delete items
      if (selectedItemIds.length > 0) {
        if (selectedItemIds.length === 1) {
          await storageService.deleteItem(selectedItemIds[0]);
          dispatch(removeItem(selectedItemIds[0]));
        } else {
          await storageService.deleteBatchItems({ itemIds: selectedItemIds });
          dispatch(removeItems(selectedItemIds));
        }
      }

      // Delete directories
      const deletePromises = selectedDirectoryIds.map(async (dirId) => {
        await storageService.deleteDirectory(dirId);
        dispatch(removeDirectory(dirId));
      });
      await Promise.all(deletePromises);

      // Refresh data
      const usage = await storageService.getStorageUsage();
      dispatch(setStorageUsage(usage));

      const currentItems = await storageService.getItems(currentDirectoryId);
      dispatch(setItems(currentItems));

      const tree = await storageService.getDirectoryTree();
      dispatch(setDirectoryTree(tree));

      dispatch(setShowDeleteConfirmModal(false));
    } catch (err) {
      console.error('Failed to delete:', err);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleClose = () => {
    dispatch(setShowDeleteConfirmModal(false));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        <div className="px-6 py-5 border-b border-secondary-200 bg-danger/5">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-danger rounded-lg">
              <HiTrash className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-xl font-semibold text-primary-900">{t('page.files.deleteConfirmModal.title')}</h3>
          </div>
        </div>

        <div className="px-6 py-5">
          <p className="text-primary-900 font-medium mb-4">
            {t('page.files.deleteConfirmModal.message')} {totalSelected} {totalSelected === 1 ? t('page.files.deleteConfirmModal.item') : t('page.files.deleteConfirmModal.items')}?
          </p>

          {selectedDirs.length > 0 && (
            <div className="bg-secondary-50 border border-secondary rounded-lg p-4 mb-4">
              <div className="flex items-start gap-2">
                <HiExclamationTriangle className="w-5 h-5 text-secondary mt-0.5 flex-shrink-0" />
                <div className="text-sm text-primary-900">
                  <p className="font-semibold mb-1">{t('page.files.deleteConfirmModal.warningTitle')}</p>
                  <p>
                    {t('page.files.delete')} {selectedDirs.length === 1 ? t('page.files.deleteConfirmModal.warningFolder') : t('page.files.deleteConfirmModal.warningFolders')} {t('page.files.deleteConfirmModal.warningMessage')}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="bg-secondary-50 rounded-xl p-4 max-h-48 overflow-y-auto border border-secondary-200">
            <ul className="space-y-2.5 text-sm">
              {selectedDirs.map((dir) => (
                <li key={`dir-${dir.id}`} className="flex items-center gap-2 text-primary-900">
                  <HiFolder className="w-4 h-4 text-secondary" />
                  <span className="font-semibold">{dir.name}</span>
                  <span className="text-gray-500">({t('page.files.deleteConfirmModal.folder')})</span>
                </li>
              ))}
              {selectedItems.map((item) => (
                <li key={`item-${item.id}`} className="flex items-center gap-2 text-primary-900">
                  <HiDocument className="w-4 h-4 text-gray-400" />
                  <span>{item.fileName}</span>
                </li>
              ))}
            </ul>
          </div>

          <p className="mt-4 text-sm text-danger font-semibold">⚠️ {t('page.files.deleteConfirmModal.cannotUndo')}</p>
        </div>

        <div className="px-6 py-4 border-t border-secondary-200 bg-secondary-50 flex justify-end gap-3">
          <Button
            type="button"
            variant="tertiary"
            onClick={handleClose}
          >
            {t('page.files.deleteConfirmModal.cancel')}
          </Button>
          <Button
            type="button"
            variant="danger-v2"
            onClick={handleConfirm}
          >
            <HiTrash className="w-4 h-4" />
            {t('page.files.deleteConfirmModal.deleteButton')} {totalSelected} {totalSelected === 1 ? t('page.files.deleteConfirmModal.item') : t('page.files.deleteConfirmModal.deleteItems')}
          </Button>
        </div>
      </div>
    </div>
  );
}
